import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Row, Col, Table, Button, Select, Typography, message, Modal, Checkbox, Input, Tooltip } from 'antd';
import { EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import AxiosOICStat from '../AxiosOICStat';
import { useSelector, useDispatch } from 'react-redux';
import { Switch } from 'antd';
import { FilterDropdown } from '../components/FilterDropdown';
import { IndicatorEditModal, setFetch, getFetch } from '../components/modals/IndicatorEdit';
import { Link } from "react-router-dom";
import { ExpandableTextView } from '../components/ExpandableTextView';
import { Divider } from 'antd';
import * as XLSX from 'xlsx';
import RoleUtils from '../utils/RoleUtils';

const { Text } = Typography;
const { Search } = Input;

export const Indicators = () => {
    const [indicators, setIndicators] = React.useState([]);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [showModalVisible, setShowModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [loading, setLoading] = React.useState(true);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isAddMode, setIsAddMode] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const user = useSelector((state) => state.user);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const importFileInputRef = useRef(null);
    const [searchValue, setSearchValue] = React.useState('');
    const [initialFetch, setInitialFetch] = useState(true);
    const indicatorsState = useSelector(state => Object.values(state.indicators.value));
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [formulaModalVisible, setFormulaModalVisible] = useState(false);
    const [selectedFormula, setSelectedFormula] = useState(null);
    const [useFormulaData, setUseFormulaData] = useState(true);

    const unitsState = useSelector((state) => state.units);
    //console.log('unitsState:', unitsState);

    const categoriesState = useSelector((state) => state.categories);
    //console.log('categoriesState:', categoriesState);

    const sourcesState = useSelector((state) => state.sources);
    //console.log('sourcesState:', sourcesState);


    // Define filter options for Units, Categories, and Sources
    const unitFilterOptions = useMemo(() => createFilterOptions(unitsState.value, 'unit_name_eng'), [unitsState.value]);
    const categoryFilterOptions = useMemo(() => createFilterOptions(categoriesState.value, 'cat_name_eng'), [categoriesState.value]);
    const sourceFilterOptions = useMemo(() => createFilterOptions(sourcesState.value, 'source_name_eng'), [sourcesState.value]);
    const aggregationMethodOptions = useMemo(generateAggregationMethodOptions, [indicators]);
    const indicatorFilterOptions = useMemo(() => createFilterOptions(indicators, 'ind_name_eng'), [indicators]);
    const indCodeFilterOptions = useMemo(() => createFilterOptions(indicators, 'ind_code'), [indicators]);

    const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState(null);
    const [selectedIsPrivate, setSelectedIsPrivate] = useState(null);
    const [selectedAggrMethod, setSelectedAggrMethod] = useState(null);

    console.log('indicators:', indicatorsState);


    // Function to create filter options from state values
    function createFilterOptions(stateValues, nameKey) {
        const uniqueNames = new Set(Object.values(stateValues).map(item => item[nameKey]));
        return Array.from(uniqueNames).map(name => ({ text: name, value: name }));
    }

    function generateAggregationMethodOptions() {
        // Assuming 'indicators' is your data array
        const uniqueMethods = new Set(indicators.map(item => item.aggr_method));
        return Array.from(uniqueMethods).map(method => ({
            text: method !== null ? method : 'None',
            value: method,
        }));
    };

    // Function to handle select all or deselect all
    const onSelectAllChange = (e) => {
        if (e.target.checked) {
            // Select all rows currently displayed in the table
            const displayedRowKeys = filteredData.map(indicator => indicator.ind_code);
            setSelectedRowKeys(displayedRowKeys);
        } else {
            // Deselect all rows
            setSelectedRowKeys([]);
        }
    };

    const downloadXLSX = async () => {
        try {
            setDownloadLoading(true);
            const response = await AxiosOICStat.get('/indicators/indicators-csv/download', {
                responseType: 'blob', // This tells Axios to handle the response as a Blob
            });

            // Use FileReader to read the blob's data
            const reader = new FileReader();
            reader.onload = (evt) => {
                const data = evt.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });

                // Assuming there is at least one worksheet in the workbook
                const wsname = workbook.SheetNames[0];
                const ws = workbook.Sheets[wsname];

                // Convert the worksheet to JSON, specifying raw format for dates
                const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });

                const newWorkbook = XLSX.utils.book_new();
                const newWs = XLSX.utils.json_to_sheet(jsonData, {
                    cellDates: true,
                    dateNF: 'yyyy-mm-dd'  // Ensure date format is consistent
                });

                XLSX.utils.book_append_sheet(newWorkbook, newWs, "Indicators");
                XLSX.writeFile(newWorkbook, 'Indicator_Data.xlsx');
            };
            reader.readAsBinaryString(response.data);
        } catch (error) {
            message.error('Failed to download XLSX');
            console.error('Download XLSX error:', error);
        } finally {
            setDownloadLoading(false);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            {
                key: 'selectAllOrNone',
                text: 'Select All/None',
                onSelect: (changeableRowKeys) => {
                    if (selectedRowKeys.length === changeableRowKeys.length) {
                        // If all are selected, deselect all
                        setSelectedRowKeys([]);
                    } else {
                        // Otherwise, select all
                        setSelectedRowKeys(changeableRowKeys);
                    }
                },
            },
        ],
        renderCell: (checked, record, index, originNode) => {
            if (index === -1) { // This is the header row
                return (
                    <Checkbox
                        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < indicators.length}
                        onChange={onSelectAllChange}
                        checked={selectedRowKeys.length === indicators.length}
                    />
                );
            }
            return originNode; // For all other rows, return the original node
        },
    };

    const hasSelected = selectedRowKeys.length > 0;

    const handleAddNew = () => {
        setSelectedRecord(null);
        setEditModalVisible(true);
        setIsAddMode(true);
    };

    // Function to reset bulk edit UI values
    const resetBulkEditUI = () => {
        setSelectedPrimaryCategory(null);
        setSelectedIsPrivate(null);
        setSelectedAggrMethod(null);
    };

    const handleDelete = (indCode) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this indicator?',
            onOk: () => {
                AxiosOICStat.delete(`indicators/${indCode}`)
                    .then(() => {
                        message.success('Indicator deleted successfully');
                        setIndicators(prevIndicators => prevIndicators.filter(ind => ind.ind_code !== indCode));
                    })
                    .catch(err => {
                        message.error('Error deleting indicator');
                    });
            }
        });
    };

    const handleSearch = (value) => {      setSearchValue(value);
      const lower = value.toLowerCase();
      const filtered = indicators.filter(ind => {
        return (
          (ind.ind_name_eng  || '')
            .toLowerCase()
            .includes(lower)
        ) || (
          ind.ind_code
            .toString()
            .includes(lower)
        );
      });
      setFilteredData(filtered);
    };

    const handleImport = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        setImportLoading(true);
        try {
            const response = await AxiosOICStat.post('indicators/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const res = response.data;
            const summary = `Import complete: ${res.updated} updated, ${res.created} created, ${res.skipped} skipped`;
            if (res.errors && res.errors.length > 0) {
                message.warning(`${summary}. ${res.errors.length} row(s) with errors.`);
                console.warn('Import errors:', res.errors);
            } else {
                message.success(summary);
            }

            // Refresh the local list after import
            const refreshed = await AxiosOICStat.get('indicators/descriptions');
            setIndicators(refreshed.data);
            setFilteredData(refreshed.data);
        } catch (error) {
            const detail = error.response && error.response.data && error.response.data.detail;
            message.error(`Import failed: ${detail || 'Please check the file format'}`);
            console.error('Import error:', error);
        } finally {
            setImportLoading(false);
            event.target.value = ''; // allow re-importing the same file
        }
    };

    const handleBulkEdit = async () => {
        let updateData = {};
        if (selectedPrimaryCategory !== null) updateData.cat_code = selectedPrimaryCategory;
        updateData.is_private = selectedIsPrivate ? 1 : 0; // Assuming your backend expects 1 or 0
        if (selectedAggrMethod !== null) updateData.aggr_method = selectedAggrMethod;
        if (selectedAggrMethod === 'None') updateData.aggr_method = null;


        // Create an array of requests, one for each selected indicator
        const updateRequests = selectedRowKeys.map(indCode =>
            AxiosOICStat.put(`indicators/${indCode}`, updateData)
        );

        try {
            // Execute all requests
            await Promise.all(updateRequests);
            setSelectedRowKeys([]);

            // Refresh the data
            // Fetch the updated list of indicators
            const response = await AxiosOICStat.get('indicators/descriptions');
            setIndicators(response.data);
            console.log("Updated Indicators", indicators);
            resetBulkEditUI();
            message.success('Indicators updated successfully');
            
        } catch (error) {
            message.error('Error updating indicators');
            resetBulkEditUI();
        }
    };


    const columns = [
        {
            title: 'Code', dataIndex: 'ind_code',
            filters: indCodeFilterOptions,
            onFilter: (value, record) => record.ind_code === value,
            sorter: (a, b) => a.ind_code - b.ind_code,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={indCodeFilterOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    numberFilter={true}
                />
            ),
            render: (indCode) => <Link to={`/indicator/${indCode}`}>{indCode}</Link>,
        },
        {
            title: 'Name',
            dataIndex: 'ind_name_eng',
            filters: indicatorFilterOptions,
            onFilter: (value, record) => record.ind_name_eng === value,
            sorter: (a, b) => a.ind_name_eng.localeCompare(b.ind_name_eng),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={indicatorFilterOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
            ellipsis: false,
               render: (indNameEng, record) => {
                   const nameToShow = record && record.is_used ? (record.optional_name || indNameEng) : indNameEng;
                   return <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}><Link to={`/indicator/${record.ind_code}`}>{nameToShow}</Link></div>;
               },
        },
        {
            title: 'Explanation', dataIndex: 'ind_eng_exp',
            ellipsis: false,
            render: (text) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}><ExpandableTextView text={text} /></div>,
        },
        {
            title: 'Unit',
            dataIndex: 'unit_code',
            filters: unitFilterOptions,
            onFilter: (value, record) => unitsState.value[record.unit_code]?.unit_name_eng === value,
            sorter: (a, b) => {
                const unitA = unitsState.value[a.unit_code]?.unit_name_eng || a.unit_code;
                const unitB = unitsState.value[b.unit_code]?.unit_name_eng || b.unit_code;
                return unitA.localeCompare(unitB);
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={unitFilterOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
            render: (unitCode) => unitsState.value[unitCode]?.unit_name_eng || unitCode,
        },
        {
            title: 'Primary Category', dataIndex: 'cat_code',
            filters: categoryFilterOptions,
            onFilter: (value, record) => categoriesState.value[record.cat_code]?.cat_name_eng === value,
            sorter: (a, b) => {
                const categoryA = categoriesState.value[a.cat_code]?.cat_name_eng || a.cat_code;
                const categoryB = categoriesState.value[b.cat_code]?.cat_name_eng || b.cat_code;
                return categoryA.localeCompare(categoryB);
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={categoryFilterOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
            ellipsis: false,
            render: (catCode) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{categoriesState.value[catCode]?.cat_name_eng || catCode}</div>,
        },
        {
            title: 'Is private?',
            dataIndex: 'is_private',
            sorter: (a, b) => a.is_private - b.is_private, // Assuming 'is_private' is a numeric value
            render: (isPrivate) => (isPrivate === 1 ? 'private' : 'non-private'),
        },
        {
            title: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Source</div>,
            dataIndex: 'source_code',
            filters: sourceFilterOptions,
            onFilter: (value, record) => sourcesState.value[record.source_code]?.source_name_eng === value,
            sorter: (a, b) => {
                const sourceA = sourcesState.value[a.source_code]?.source_name_eng || a.source_code;
                const sourceB = sourcesState.value[b.source_code]?.source_name_eng || b.source_code;
                return String(sourceA || '').localeCompare(String(sourceB || ''));
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={sourceFilterOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
            ellipsis: false,
            render: (sourceCode) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{sourcesState.value[sourceCode]?.source_name_eng || sourceCode}</div>,
        },
        {
            title: 'Indicator Formula', dataIndex: 'formula',
            filters: aggregationMethodOptions,
            onFilter: (value, record) => record.formula === value,
            sorter: (a, b) => {
                const methodA = a.formula || 'None';
                const methodB = b.formula || 'None';
                return methodA.localeCompare(methodB);
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={aggregationMethodOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
            ellipsis: false,
            render: (formula) => {
                if (!formula) return '-';
                
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 400 }}>{formula}</div>
                        <Button 
                            size="small" 
                            icon={<EyeOutlined />} 
                            onClick={() => {
                                setSelectedFormula(formula);
                                setFormulaModalVisible(true);
                            }}
                        />
                    </div>
                );
            },
        },
        {
            title: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Aggregation Method</div>, dataIndex: 'aggr_method',
            filters: aggregationMethodOptions,
            onFilter: (value, record) => record.aggr_method === value,
            sorter: (a, b) => {
                const methodA = a.aggr_method || 'None';
                const methodB = b.aggr_method || 'None';
                return methodA.localeCompare(methodB);
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={aggregationMethodOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
            ellipsis: false,
            render: (aggrMethod) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{aggrMethod !== null ? aggrMethod : '-'}</div>,
        },
        {
            title: 'Aggregation Formula', dataIndex: 'aggr_formula',
            filters: aggregationMethodOptions,
            onFilter: (value, record) => record.aggr_formula === value,
            sorter: (a, b) => {
                const methodA = a.aggr_formula || 'None';
                const methodB = b.aggr_formula || 'None';
                return methodA.localeCompare(methodB);
            },
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={aggregationMethodOptions}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
            ellipsis: false,
            render: (aggrFormula) => {
                if (!aggrFormula) return '-';
            
                const indicatorCodeMatches = aggrFormula.match(/ind(\d+)/g);
            
                if (!indicatorCodeMatches) {
                    return aggrFormula;
                }
            
                const indicatorNames = [];
            
                indicatorCodeMatches.forEach(match => {
                    const code = match.replace('ind', '');
                    const indicator = indicatorsState.find(ind => ind.ind_code.toString() === code);
                    if (indicator) {
                        indicatorNames.push(indicator.ind_name_eng);
                    } else {
                        indicatorNames.push(match);
                    }
                });
            
                if (indicatorNames.length === 1) {
                    return indicatorNames[0];
                }
            
                return (
                    <ul style={{ margin: 0, paddingLeft: '20px', whiteSpace: 'normal' }}>
                        {indicatorNames.map((name, index) => (
                            <li key={index}>{name}</li>
                        ))}
                    </ul>
                );
            },
        },
        {
            title: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Competence Rate</div>,
            dataIndex: 'comp_rate',
            ellipsis: false,
            render: (comp_rate) => {
                if (comp_rate === null || comp_rate === undefined) return '-';
                const formattedRate = (comp_rate * 100).toFixed(2);

                let color;
                if (comp_rate < 0.30) {
                    color = 'red';
                } else if (comp_rate >= 0.30 && comp_rate <= 0.70) {
                    color = 'orange';
                } else {
                    color = 'green';
                }

                return <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}><span style={{ color: color }}>{formattedRate}%</span></div>;
            }
        },
        {
            title: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Available Years</div>,
            dataIndex: 'available_years',
            ellipsis: false,
            render: (available_years) => {
                if (available_years === null || available_years === undefined) {
                    return '-';
                }
                return <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{available_years.replace(/([\(\)])/g, '')}</div>;
            }
        },        
        {
            title: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Last Update Date</div>,
            dataIndex: 'operation_datetime',
            ellipsis: false,
            sorter: (a, b) => new Date(a.operation_datetime) - new Date(b.operation_datetime),
            render: (date) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{date ? new Date(date).toLocaleDateString() : '-'}</div>,
        },
        {
            title: 'Actions',
            dataIndex: '',
            key: 'actions',
            render: (text, record) => (
                <>
                    <span style={{ display: "flex" }}>
                        <Button icon={<EyeOutlined />} onClick={() => handleShow(record)}></Button>
                        {RoleUtils.canEditRecords(user.role) &&
                            <>
                                <Button style={{ marginLeft: 8 }} type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)}></Button>
                                <Button style={{ marginLeft: 8 }} type="danger" icon={<DeleteOutlined />} onClick={() => handleDelete(record.ind_code)}></Button>
                            </>
                        }
                    </span>
                </>
            ),
        },
    ];


    useEffect(() => {
      if (editModalVisible) return;

      if (initialFetch && indicatorsState.length > 0) {
        setIndicators(indicatorsState);
        setFilteredData(indicatorsState);
        setLoading(false);
        setInitialFetch(false); 
      }
    }, [
      editModalVisible,   
      indicatorsState,
      initialFetch
    ]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await AxiosOICStat.get('/settings/indicator-data-source');
                if (mounted) setUseFormulaData(data.use_formula_data);
            } catch (e) {
                console.error('Failed to load indicator data source setting:', e);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const handleDataSourceChange = async (checked) => {
        const previous = useFormulaData;
        setUseFormulaData(checked);
        try {
            await AxiosOICStat.put('/settings/indicator-data-source', { use_formula_data: checked });
            message.success(checked ? 'Formula data enabled' : 'Stored system data enabled');
        } catch (e) {
            setUseFormulaData(previous);
            message.error('Failed to update data source setting.');
            console.error('Update data source error:', e);
        }
    };

    const handleEdit = (record) => {
        setIsAddMode(false);
        setSelectedRecord(record);
        setEditModalVisible(true);
    };

    const handleEditSave = (updatedIndicator) => {
      // Update master list
      const newIndicators = indicators.map(ind =>
        ind.ind_code === updatedIndicator.ind_code
          ? updatedIndicator
          : ind
      );
      setIndicators(newIndicators);

      // Re-apply current search/filter
      const lower = searchValue.toLowerCase();
      const newFiltered = newIndicators.filter(ind => {
        return (
          (ind.ind_name_eng || '').toLowerCase().includes(lower) ||
          ind.ind_code.toString().includes(lower)
        );
      });
      setFilteredData(newFiltered);

      setEditModalVisible(false);
      message.success('Indicator updated successfully');
    };
    


    const handleShow = (record) => {
        setSelectedRecord(record);
        setShowModalVisible(true);
    };

    const handleEditModalCancel = () => {
        setEditModalVisible(false);
    };

    const handleShowModalCancel = () => {
        setShowModalVisible(false);
    };

    const bulkEditUI = hasSelected ? (
        <Row style={{ marginBottom: 16 }}>
            <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
                <Text>Primary Category:</Text>
                <Select
                    placeholder="Select Primary Category"
                    onChange={value => setSelectedPrimaryCategory(value)}
                    value={selectedPrimaryCategory}
                    style={{ width: '75%', marginLeft: 8 }}
                >
                    {Object.values(categoriesState.value).map(category => (
                        <Select.Option key={category.cat_code} value={category.cat_code}>
                            {category.cat_name_eng}
                        </Select.Option>
                    ))}
                </Select>
            </Col>
            <Divider type="vertical" style={{ height: 'auto' }} />
            <Col span={2} style={{ display: 'flex', alignItems: 'center' }}>
                <Text style={{ marginRight: 8 }}>Is Private:</Text>
                <Switch
                    checked={selectedIsPrivate}
                    onChange={checked => setSelectedIsPrivate(checked)}
                />
            </Col>
            <Divider type="vertical" style={{ height: 'auto' }} />
            <Col span={10} style={{ display: 'flex', alignItems: 'center' }}>
                <Text style={{ marginRight: 8 }}>Aggregation Method:</Text>
                <Select
                    placeholder="Select Aggregation Method"
                    style={{ width: '75%', marginLeft: 8 }}
                    onChange={value => setSelectedAggrMethod(value)}
                    value={selectedAggrMethod}
                >
                    <Select.Option value={"None"}>None</Select.Option>
                    <Select.Option value="Sum">Sum</Select.Option>
                    <Select.Option value="Weighted">Weighted</Select.Option>
                    <Select.Option value="Median">Median</Select.Option>
                    <Select.Option value="Average">Average</Select.Option>
                </Select>
            </Col>
            <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button type="primary" onClick={handleBulkEdit}>
                    Apply Changes
                </Button>
            </Col>
        </Row>
    ) : null;

    return (
        <Row>
            {indicators &&
                <Col style={{ marginBottom: "10px", display: 'flex', alignItems: 'center', gap: '8px' }} xs={12}>
                    {RoleUtils.canEditRecords(user.role) && <Button type="primary" onClick={handleAddNew}>Add New Indicator</Button>}
                    <Button onClick={downloadXLSX} type="primary" style={{ marginLeft: 8 }} disabled={downloadLoading}> 
                        {downloadLoading ? 'Exporting...' : 'Export to Excel'}
                    </Button>
                    <Button onClick={() => importFileInputRef.current && importFileInputRef.current.click()} type="primary" style={{ marginLeft: 8 }} loading={importLoading}>
                        Import from Excel
                    </Button>
                    <input
                        ref={importFileInputRef}
                        type="file"
                        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        style={{ display: 'none' }}
                        onChange={handleImport}
                    />
                    {RoleUtils.canManageMetadata(user.role) && (
                        <span style={{ marginLeft: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                            <Switch
                                checked={useFormulaData}
                                onChange={handleDataSourceChange}
                            />
                            <Tooltip title="On: indicator values are calculated from formulas on the fly. Off: stored database values are used.">
                                <Text>Use formula data</Text>
                            </Tooltip>
                        </span>
                    )}
                </Col>
                
            }
            <Search
                                placeholder="Search"
                                allowClear
                                value={searchValue}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{ marginBottom: 10 }}
                            />
                <Col xs={24}>
                    {RoleUtils.canEditRecords(user.role) && bulkEditUI}
                    <div style={{ width: '100%' }}>
                        <Table
                            rowKey="ind_code"
                            rowSelection={RoleUtils.isResearcher(user.role) && rowSelection}
                            dataSource={filteredData}
                            onRow={(record) => ({ style: record.is_private === 1 ? { color: '#bfbfbf' } : undefined })}
                            columns={columns.map(col => ({ ...col, ellipsis: col.ellipsis === false ? false : true }))}
                            size="small"
                            tableLayout="fixed"
                            style={{ width: '100%' }}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                showSizeChanger: true,
                                onChange: (page, size) => setPagination({ current: page, pageSize: size }),
                            }}
                            loading={{ spinning: loading, tip: 'Loading' }}
                        />
                    </div>
                </Col>


            <IndicatorEditModal
                visible={editModalVisible}
                onCancel={handleEditModalCancel}
                onEdit={handleEditSave}
                record={selectedRecord}
                editable={true}
                isAddMode={isAddMode}
            />

            <Modal
                title="Formula Details"
                open={formulaModalVisible}
                onCancel={() => setFormulaModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setFormulaModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
            >
                {selectedFormula && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Compact Format:</Text>
                            <div style={{ 
                                padding: '8px 12px', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: 4,
                                marginTop: 8,
                                fontFamily: 'monospace'
                            }}>
                                {selectedFormula}
                            </div>
                        </div>
                        <div>
                            <Text strong>Expanded Format:</Text>
                            <div style={{ 
                                padding: '8px 12px', 
                                backgroundColor: '#f0f7ff', 
                                borderRadius: 4,
                                marginTop: 8,
                                lineHeight: 1.8
                            }}>
                                {(() => {
                                    let displayFormula = selectedFormula;
                                    const indicatorCodeMatches = selectedFormula.match(/ind(\d+)/g);
                                    
                                    if (indicatorCodeMatches) {
                                        indicatorCodeMatches.forEach(match => {
                                            const code = match.replace('ind', '');
                                            const indicator = indicatorsState.find(ind => ind.ind_code.toString() === code);
                                            if (indicator) {
                                                displayFormula = displayFormula.replace(match, `[${indicator.ind_name_eng}]`);
                                            }
                                        });
                                    }
                                    
                                    return displayFormula;
                                })()}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </Row>
    );
};